/**
 * The Property-development-schedule Gantt as a DOM/CSS table-Gantt (INS-10671, reversing
 * the tech-spec §5.1 SciChart decision — product-owner authorized). It is a scrolling list
 * of fixed-height rows: each area is a header row (id + "N properties" pill + right-edge
 * expand chevron), and each expanded slot is a property row whose track maps its effective
 * dates to left%/width% against the shared time domain via {@link toPercent}. Rows are
 * virtualized (TanStack `@tanstack/react-virtual`, the same library the data-table uses) so
 * thousands of expanded slots stay smooth — only the visible window is in the DOM.
 *
 * Interactions preserve the SciChart callbacks exactly so both consumers (the eval modal
 * and the DPS config-side preview) keep working unchanged:
 *   - drag an editable milestone marker → pixel→date (inverse of `toPercent`), clamped to
 *     the lifecycle-order {@link neighborBounds} ∩ domain, committed via `onDragCommit`;
 *   - ≥2 editable markers stacked at one date → `onStackedClick` opens the picker;
 *   - click / shift-click a marker, or box-select (marquee) the track surface → the shared
 *     selection via `onSelectionChange` (replace, or union with shift);
 *   - the per-area chevron → `onToggleGroup`.
 *
 * Interactive pixel zoom/pan is intentionally deferred (the mock's zoom buttons are not the
 * core interaction); the domain is owned by the parent via {@link ScheduleGanttProps.domain}
 * (from `domainRange`, future-only narrowing included), so `onWindowChange` stays wired but
 * only future zoom work would fire it.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import moment from 'moment';
import { useVirtualizer } from '@tanstack/react-virtual';
import { Tag } from '@carbon/react';
import { ChevronDown, ChevronRight, Locked } from '@carbon/react/icons';
import { formatNumber } from 'utils/number';
import {
  MARKER_COLORS,
  MILESTONE_LABELS,
  MILESTONE_ORDER,
  PHASE_COLORS,
} from './constants';
import {
  clamp,
  clampDisplayMs,
  isEditablePoint,
  markerState,
  neighborBounds,
  parseIsoDay,
  phaseSegments,
  rowLabelLines,
  selectionKey,
  snapMsToMonth,
  toIsoDay,
  toPercent,
  windowLabel,
} from './utils';
import {
  selectionToPoints,
  stackAt,
  stackOpensPicker,
  type SelectionSet,
} from './selection-model';
import type {
  GroupHeader,
  MergedDate,
  MilestoneKey,
  ScheduleRowModel,
  SlotId,
  VisibleRow,
} from './types';
import styles from './schedule-gantt.module.scss';

// Fixed row heights (mock-exact): a unit header is 31px, a property row 34px.
const UNIT_HEADER_HEIGHT = 31;
const PROPERTY_ROW_HEIGHT = 34;
// Pointer travel (px) before a marker mousedown counts as a drag rather than a click.
const DRAG_THRESHOLD = 3;
// Target count of year labels across the axis; drives the tick step.
const YEAR_LABEL_TARGET = 7;
// Horizontal-scroll mode only: each year of the domain gets this many pixels, so a long
// date range extends past the viewport and scrolls; short ranges still stretch to fill via
// the CSS `min-width: 100%` floor. Tune timeline density here.
const PX_PER_YEAR = 100;
const MS_PER_YEAR = 365.25 * 24 * 60 * 60 * 1000;
// A shared empty participant set for single-marker drags (no per-mousedown allocation).
const EMPTY_PARTICIPANTS: ReadonlySet<string> = new Set<string>();

// ---------------------------------------------------------------------------
// Props (identical to the SciChart era — both consumers depend on this shape)
// ---------------------------------------------------------------------------

export interface ScheduleGanttProps {
  model: ScheduleRowModel;
  domain: { startMs: number; endMs: number };
  selection: SelectionSet;
  order: MilestoneKey[];
  /**
   * Snap a dragged marker to the modeled month boundary (Defect 5), so a commit lands on
   * the 1st like every modeled date rather than a mid-month day. Opt-in and defaulting off,
   * so the shared DPS config-side preview keeps its day-precision drag until it opts in.
   */
  snapToMonth?: boolean;
  onDragCommit: (slotId: SlotId, field: MilestoneKey, isoDay: string) => void;
  /**
   * Multi-marker drag (INS-10671): when ≥2 markers are selected and the user grabs one of
   * them, the whole selection shifts by the same delta the grabbed marker moved (`others`
   * = every selected point except the grabbed one; `deltaMs` = its clamped day-delta).
   * Optional — a consumer that omits it keeps the single-marker `onDragCommit` behavior for
   * every drag, so the DPS config-side preview is untouched until it opts in.
   */
  onMultiDragCommit?: (
    primary: { slotId: SlotId; field: MilestoneKey; iso: string },
    others: Array<{ slotId: SlotId; field: MilestoneKey }>,
    deltaMs: number
  ) => void;
  /**
   * Opt-in stacked-milestone picker: when two editable milestones share a date, grabbing
   * them fires this to open a disambiguation picker. Optional — a consumer that omits it
   * (the eval schedule dialog, where the picker is not in the design) has a grab drag the
   * clicked marker directly, with no popup. The DPS config-side preview still wires it.
   */
  onStackedClick?: (slotId: SlotId, targetIso: string) => void;
  onSelectionChange: (
    points: Array<{ slotId: SlotId; field: MilestoneKey }>
  ) => void;
  /** Toggle a unit group's expansion (groupId = unit id, or null for "Unscheduled"). */
  onToggleGroup: (groupId: number | null) => void;
  onWindowChange?: (startMs: number, endMs: number) => void;
  height?: number;
  /**
   * Enable a fixed-density, horizontally-scrolling timeline with a frozen label column and
   * a scroll-synced year axis (eval schedule dialog only). Off by default, so the shared DPS
   * config-side preview keeps its fit-to-width layout untouched.
   */
  horizontalScroll?: boolean;
}

// ---------------------------------------------------------------------------
// Flat render list — unit headers interleaved with their expanded property rows
// ---------------------------------------------------------------------------

type GanttItem =
  { kind: 'group'; header: GroupHeader } | { kind: 'row'; visible: VisibleRow };

// The group key a visible row belongs to (null = the "Unscheduled" pseudo-group).
function rowGroupKey(row: VisibleRow['row']): number | null {
  return row.unscheduled ? null : (row.groupId ?? null);
}

interface SelectedPoint {
  slotId: SlotId;
  field: MilestoneKey;
}

/**
 * The live drag preview. `primary` is the grabbed marker's previewed date. On a
 * multi-marker drag `participants` holds the selection keys of the OTHER markers that move
 * with it and `deltaMs` is the grabbed marker's clamped day-delta, so each participant
 * row can preview its own shifted position (clamped to its own bounds). `participants` is
 * empty and `deltaMs` unused for a single-marker drag.
 */
interface DragPreview {
  primary: { slotId: SlotId; field: MilestoneKey; iso: string };
  deltaMs: number;
  participants: ReadonlySet<string>;
}

interface MarkerSession {
  kind: 'marker';
  slotId: SlotId;
  field: MilestoneKey;
  editable: boolean;
  trueIso: string;
  rowDates: Record<MilestoneKey, MergedDate>;
  trackLeft: number;
  trackWidth: number;
  shiftKey: boolean;
  startX: number;
  moved: boolean;
  // Multi-marker drag: set when the grabbed marker is part of a ≥2 selection and the
  // consumer wired `onMultiDragCommit`. `others` are the OTHER selected points that shift
  // with the grabbed one; `participants` is their selection-key set (for the live preview).
  multi: boolean;
  others: SelectedPoint[];
  participants: ReadonlySet<string>;
}

interface MarqueeSession {
  kind: 'marquee';
  x0: number;
  y0: number;
  shiftKey: boolean;
}

type Session = MarkerSession | MarqueeSession;

interface MarqueeRect {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Renders the DOM Gantt and keeps it in sync with the row model, domain, and selection.
 *
 * @param {ScheduleGanttProps} props - Row model, domain, selection, and edit callbacks.
 * @return {JSX.Element} The chart.
 */
export function ScheduleGantt({
  model,
  domain,
  selection,
  order,
  snapToMonth = false,
  onDragCommit,
  onMultiDragCommit,
  onStackedClick,
  onSelectionChange,
  onToggleGroup,
  height = 480,
  horizontalScroll = false,
}: ScheduleGanttProps) {
  const ganttRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const axisInnerRef = useRef<HTMLDivElement>(null);
  const sessionRef = useRef<Session | null>(null);

  // The live drag preview — the grabbed marker's date, plus (on a multi-drag) the delta +
  // participant set so every selected marker previews its shift together.
  const [dragPreview, setDragPreview] = useState<DragPreview | null>(null);
  const [marquee, setMarquee] = useState<MarqueeRect | null>(null);

  // Flat, ordered render items: each unit header, then (if expanded) its property rows.
  const items = useMemo<GanttItem[]>(() => {
    const byGroup = new Map<number | null, VisibleRow[]>();
    for (const visible of model.visibleRows) {
      const key = rowGroupKey(visible.row);
      const list = byGroup.get(key);
      if (list) {
        list.push(visible);
      } else {
        byGroup.set(key, [visible]);
      }
    }
    const out: GanttItem[] = [];
    for (const header of model.groupHeaders) {
      out.push({ kind: 'group', header });
      if (header.expanded) {
        for (const visible of byGroup.get(header.groupId) ?? []) {
          out.push({ kind: 'row', visible });
        }
      }
    }
    return out;
  }, [model.groupHeaders, model.visibleRows]);

  const virtualizer = useVirtualizer({
    count: items.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: (index) => {
      return items[index].kind === 'group'
        ? UNIT_HEADER_HEIGHT
        : PROPERTY_ROW_HEIGHT;
    },
    getItemKey: (index) => {
      const item = items[index];
      return item.kind === 'group'
        ? `g-${item.header.groupId ?? 'unscheduled'}`
        : `r-${item.visible.row.slotId}`;
    },
    overscan: 12,
  });

  // Year tick positions — shared by the axis header labels and each row's gridlines so
  // the labels sit exactly over the gridlines (mock's aligned year ticks).
  const yearTicks = useMemo(() => {
    const startYear = new Date(domain.startMs).getUTCFullYear();
    const endYear = new Date(domain.endMs).getUTCFullYear();
    const span = Math.max(1, endYear - startYear);
    const step = Math.max(1, Math.ceil(span / YEAR_LABEL_TARGET));
    const ticks: Array<{ year: number; pct: number }> = [];
    for (let year = startYear; year <= endYear; year += step) {
      const ms = Date.UTC(year, 0, 1);
      ticks.push({ year, pct: toPercent(ms, domain.startMs, domain.endMs) });
    }
    return ticks;
  }, [domain.startMs, domain.endMs]);

  // Timeline pixel width in horizontal-scroll mode: a fixed density per year so a long
  // domain overflows the viewport (short domains stretch to fill via CSS). Fed to the CSS
  // as `--timeline-px`; ignored in the default fit-to-width layout.
  const timelinePx = useMemo(
    () =>
      Math.ceil(
        Math.max(1, (domain.endMs - domain.startMs) / MS_PER_YEAR) * PX_PER_YEAR
      ),
    [domain.startMs, domain.endMs]
  );

  const rangeLabel = windowLabel(domain.startMs, domain.endMs);

  // ---- interaction ---------------------------------------------------------
  // Fresh closures each render, invoked through stable window wrappers, so drag/marquee
  // math always reads the latest domain/order/selection without stale-closure bugs.
  const moveRef = useRef<(event: MouseEvent) => void>(() => undefined);
  const upRef = useRef<(event: MouseEvent) => void>(() => undefined);
  const winMove = useRef((event: MouseEvent) => moveRef.current(event)).current;
  const winUp = useRef((event: MouseEvent) => upRef.current(event)).current;

  const pixelToIso = useCallback(
    (clientX: number, session: MarkerSession): string => {
      const frac =
        session.trackWidth > 0
          ? (clientX - session.trackLeft) / session.trackWidth
          : 0;
      const rawMs = domain.startMs + frac * (domain.endMs - domain.startMs);
      // Snap to the modeled month boundary (Defect 5) BEFORE clamping, so a dragged marker
      // lands on the same month grid as every modeled date instead of a mid-month day; the
      // clamp then keeps it inside the lifecycle-order neighbor bounds.
      const snappedMs = snapToMonth ? snapMsToMonth(rawMs) : rawMs;
      const bounds = neighborBounds(session.rowDates, session.field, order);
      const lo = Math.max(bounds.minMs, domain.startMs);
      const hi = Math.min(bounds.maxMs, domain.endMs);
      return toIsoDay(clamp(snappedMs, lo, hi));
    },
    [domain.startMs, domain.endMs, order, snapToMonth]
  );

  const collectMarkersInRect = useCallback(
    (rect: { left: number; right: number; top: number; bottom: number }) => {
      const scope = scrollRef.current;
      if (!scope) {
        return [];
      }
      const points: SelectedPoint[] = [];
      scope.querySelectorAll<HTMLElement>('[data-marker]').forEach((el) => {
        const box = el.getBoundingClientRect();
        const cx = box.left + box.width / 2;
        const cy = box.top + box.height / 2;
        if (
          cx >= rect.left &&
          cx <= rect.right &&
          cy >= rect.top &&
          cy <= rect.bottom
        ) {
          const { slotId, field } = el.dataset;
          if (slotId && field) {
            points.push({ slotId, field: field as MilestoneKey });
          }
        }
      });
      return points;
    },
    []
  );

  const endSession = useCallback(() => {
    sessionRef.current = null;
    window.removeEventListener('mousemove', winMove);
    window.removeEventListener('mouseup', winUp);
    setDragPreview(null);
    setMarquee(null);
  }, [winMove, winUp]);

  moveRef.current = (event: MouseEvent) => {
    const session = sessionRef.current;
    if (!session) {
      return;
    }
    if (session.kind === 'marquee') {
      setMarquee({
        x0: session.x0,
        y0: session.y0,
        x1: event.clientX,
        y1: event.clientY,
      });
      return;
    }
    if (!session.editable) {
      return;
    }
    if (
      !session.moved &&
      Math.abs(event.clientX - session.startX) < DRAG_THRESHOLD
    ) {
      return;
    }
    session.moved = true;
    const iso = pixelToIso(event.clientX, session);
    const trueMs = parseIsoDay(session.trueIso) ?? 0;
    setDragPreview({
      primary: { slotId: session.slotId, field: session.field, iso },
      deltaMs: (parseIsoDay(iso) ?? trueMs) - trueMs,
      participants: session.participants,
    });
  };

  upRef.current = (event: MouseEvent) => {
    const session = sessionRef.current;
    endSession();
    if (!session) {
      return;
    }
    if (session.kind === 'marquee') {
      const hits = collectMarkersInRect({
        left: Math.min(session.x0, event.clientX),
        right: Math.max(session.x0, event.clientX),
        top: Math.min(session.y0, event.clientY),
        bottom: Math.max(session.y0, event.clientY),
      });
      onSelectionChange(
        session.shiftKey
          ? dedupePoints([...selectionToPoints(selection), ...hits])
          : hits
      );
      return;
    }
    if (session.editable && session.moved) {
      const iso = pixelToIso(event.clientX, session);
      if (iso === session.trueIso) {
        return;
      }
      // Multi-marker drag: shift the whole selection by the grabbed marker's delta; the
      // consumer clamps each other point to its own bounds. Otherwise a lone marker moves.
      if (session.multi && onMultiDragCommit) {
        const trueMs = parseIsoDay(session.trueIso) ?? 0;
        const deltaMs = (parseIsoDay(iso) ?? trueMs) - trueMs;
        onMultiDragCommit(
          { slotId: session.slotId, field: session.field, iso },
          session.others,
          deltaMs
        );
      } else {
        onDragCommit(session.slotId, session.field, iso);
      }
      return;
    }
    // A click (no drag) selects the point: replace, or union under shift.
    const point: SelectedPoint = {
      slotId: session.slotId,
      field: session.field,
    };
    onSelectionChange(
      session.shiftKey
        ? dedupePoints([...selectionToPoints(selection), point])
        : [point]
    );
  };

  const beginMarkerSession = useCallback(
    (
      event: React.MouseEvent,
      visible: VisibleRow,
      field: MilestoneKey,
      merged: MergedDate
    ) => {
      if (event.button !== 0) {
        return;
      }
      // Markers own their mousedown so the track's marquee handler never also fires.
      event.stopPropagation();
      event.preventDefault();
      const trueIso = merged.value as string;
      let dragField = field;
      // The stacked-milestone picker is opt-in (a consumer wires `onStackedClick`). When it
      // is wired and ≥2 editable milestones overlap on this date, a click can't say which
      // one you meant → open the picker to disambiguate. EXCEPT once a pick has resolved it:
      // handlePick sets the chosen milestone as the selection, so if exactly one editable
      // member of the stack is selected, drag THAT one (without this a stacked marker could
      // never move — every grab would reopen the picker). The eval schedule dialog omits
      // `onStackedClick` (the picker is not in its design), so a grab just drags the clicked
      // marker — the topmost one at that date — with no popup.
      if (onStackedClick) {
        const stack = stackAt(visible.dates, trueIso);
        if (stackOpensPicker(stack)) {
          const selectedInStack = stack
            .filter((member) => !member.locked)
            .map((member) => member.field)
            .filter((f) => selection.has(selectionKey(visible.row.slotId, f)));
          if (selectedInStack.length !== 1) {
            onStackedClick(visible.row.slotId, trueIso);
            return;
          }
          dragField = selectedInStack[0];
        }
      }
      // Drag the disambiguated field's own merged date, not the clicked marker's — the
      // click may have landed on a different (even locked) overlapping marker.
      const dragMerged = visible.dates[dragField];
      // Multi-marker drag: only when the consumer wired it, the grabbed marker is itself
      // selected, and ≥1 OTHER point is selected too. Grabbing an UNSELECTED marker (even
      // with a selection present) stays a single drag, matching the "grab one of the
      // selected items" convention.
      const primaryKey = selectionKey(visible.row.slotId, dragField);
      const others = selectionToPoints(selection).filter(
        (point) => selectionKey(point.slotId, point.field) !== primaryKey
      );
      const multi =
        Boolean(onMultiDragCommit) &&
        selection.has(primaryKey) &&
        others.length > 0;
      const track = (event.currentTarget as HTMLElement).closest<HTMLElement>(
        '[data-track]'
      );
      const box = track?.getBoundingClientRect();
      sessionRef.current = {
        kind: 'marker',
        slotId: visible.row.slotId,
        field: dragField,
        editable: isEditablePoint(dragMerged),
        trueIso,
        rowDates: visible.dates,
        trackLeft: box?.left ?? 0,
        trackWidth: box?.width ?? 0,
        shiftKey: event.shiftKey,
        startX: event.clientX,
        moved: false,
        multi,
        others: multi ? others : [],
        participants: multi
          ? new Set(
              others.map((point) => selectionKey(point.slotId, point.field))
            )
          : EMPTY_PARTICIPANTS,
      };
      window.addEventListener('mousemove', winMove);
      window.addEventListener('mouseup', winUp);
    },
    [onMultiDragCommit, onStackedClick, selection, winMove, winUp]
  );

  const handleSurfaceMouseDown = useCallback(
    (event: React.MouseEvent) => {
      if (event.button !== 0) {
        return;
      }
      // Marquee starts only on the track surface (markers stopPropagation before here).
      if (!(event.target as HTMLElement).closest('[data-track]')) {
        return;
      }
      sessionRef.current = {
        kind: 'marquee',
        x0: event.clientX,
        y0: event.clientY,
        shiftKey: event.shiftKey,
      };
      setMarquee({
        x0: event.clientX,
        y0: event.clientY,
        x1: event.clientX,
        y1: event.clientY,
      });
      window.addEventListener('mousemove', winMove);
      window.addEventListener('mouseup', winUp);
    },
    [winMove, winUp]
  );

  // Release any in-flight window listeners if the component unmounts mid-gesture.
  useEffect(
    () => () => {
      window.removeEventListener('mousemove', winMove);
      window.removeEventListener('mouseup', winUp);
    },
    [winMove, winUp]
  );

  const marqueeStyle = useMemo(() => {
    if (!marquee) {
      return undefined;
    }
    const origin = ganttRef.current?.getBoundingClientRect();
    const ox = origin?.left ?? 0;
    const oy = origin?.top ?? 0;
    return {
      left: Math.min(marquee.x0, marquee.x1) - ox,
      top: Math.min(marquee.y0, marquee.y1) - oy,
      width: Math.abs(marquee.x1 - marquee.x0),
      height: Math.abs(marquee.y1 - marquee.y0),
    };
  }, [marquee]);

  // Mirror the body's horizontal scroll onto the out-of-scroll axis header so the year
  // labels stay aligned over the row gridlines. Imperative (no React re-render per frame).
  const handleScrollSync = useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      const inner = axisInnerRef.current;
      if (inner) {
        inner.style.transform = `translateX(${-event.currentTarget
          .scrollLeft}px)`;
      }
    },
    []
  );

  const virtualItems = virtualizer.getVirtualItems();

  return (
    <div
      ref={ganttRef}
      className={classNames(styles.gantt, {
        [styles.scrollX]: horizontalScroll,
      })}
      style={
        horizontalScroll
          ? ({ ['--timeline-px']: `${timelinePx}px` } as React.CSSProperties)
          : undefined
      }
    >
      {/* Year-axis header (kept outside the scroll region so it stays put while rows
          scroll, and so the virtualizer's row offsets start cleanly at 0). */}
      <div className={styles.axisHeader}>
        <div className={styles.axisLabelCol}>
          <span className={styles.axisUnits}>
            {formatNumber({
              value: model.groupCount,
              minPrecision: 0,
              includeCommas: true,
            })}{' '}
            {model.groupCount === 1 ? 'Area' : 'Areas'}
          </span>
          <span className={styles.axisRange}>({rangeLabel})</span>
        </div>
        <div className={styles.axisTrack}>
          <div ref={axisInnerRef} className={styles.axisTrackInner}>
            {yearTicks.map((tick) => (
              <span
                key={tick.year}
                className={styles.axisYear}
                style={{
                  left: `${tick.pct}%`,
                  // Left-align the first tick, right-align the last, center the rest so no
                  // label clips at the track edges.
                  transform:
                    tick.pct <= 0
                      ? 'translateY(-50%)'
                      : tick.pct >= 100
                        ? 'translate(-100%, -50%)'
                        : 'translate(-50%, -50%)',
                }}
              >
                {tick.year}
              </span>
            ))}
          </div>
        </div>
      </div>

      <div
        ref={scrollRef}
        className={styles.scroll}
        style={{ maxHeight: `${height}px` }}
        role="group"
        aria-label="Property development schedule. Drag a milestone marker to change its date, or box-select several markers to move them together. The Table view is the keyboard-accessible equivalent."
        onMouseDown={handleSurfaceMouseDown}
        onScroll={horizontalScroll ? handleScrollSync : undefined}
      >
        <div
          className={styles.viewport}
          style={{ height: `${virtualizer.getTotalSize()}px` }}
        >
          {virtualItems.map((virtualRow) => {
            const item = items[virtualRow.index];
            return (
              <div
                key={virtualRow.key}
                className={styles.virtualRow}
                style={{
                  height: `${virtualRow.size}px`,
                  transform: `translateY(${virtualRow.start}px)`,
                }}
              >
                {item.kind === 'group' ? (
                  <UnitHeaderRow
                    header={item.header}
                    onToggleGroup={onToggleGroup}
                  />
                ) : (
                  <PropertyRow
                    visible={item.visible}
                    domain={domain}
                    selection={selection}
                    order={order}
                    yearTicks={yearTicks}
                    dragPreview={dragPreview}
                    onMarkerMouseDown={beginMarkerSession}
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>

      {marquee && <div className={styles.marquee} style={marqueeStyle} />}
    </div>
  );
}

// Dedupes `{slotId, field}` points by their selection key.
function dedupePoints(points: SelectedPoint[]): SelectedPoint[] {
  const seen = new Set<string>();
  const out: SelectedPoint[] = [];
  for (const point of points) {
    const key = selectionKey(point.slotId, point.field);
    if (!seen.has(key)) {
      seen.add(key);
      out.push(point);
    }
  }
  return out;
}

// ---------------------------------------------------------------------------
// Row sub-components
// ---------------------------------------------------------------------------

/**
 * An area header row: the expand/collapse chevron on the left, then the area name + "N
 * properties" pill. Collapsed areas render this row only (no bar), per the mock.
 *
 * @param {object} props - The group header and the toggle callback.
 * @return {JSX.Element} The header row.
 */
function UnitHeaderRow({
  header,
  onToggleGroup,
}: {
  header: GroupHeader;
  onToggleGroup: (groupId: number | null) => void;
}) {
  return (
    <div className={styles.unitHeader}>
      <div className={styles.unitHeaderInner}>
        <button
          type="button"
          className={styles.chevron}
          aria-label={`${header.expanded ? 'Collapse' : 'Expand'} ${
            header.name
          }`}
          aria-expanded={header.expanded}
          onClick={() => onToggleGroup(header.groupId)}
        >
          {header.expanded ? (
            <ChevronDown size={16} />
          ) : (
            <ChevronRight size={16} />
          )}
        </button>
        <span className={styles.unitName}>{header.name}</span>
        <Tag type="gray" className={styles.unitPill}>
          {formatNumber({
            value: header.slotCount,
            minPrecision: 0,
            includeCommas: true,
          })}{' '}
          {header.slotCount === 1 ? 'property' : 'properties'}
        </Tag>
      </div>
    </div>
  );
}

/**
 * One property (slot) row: a two-line label column (well name + `API · formation`, with a
 * lock glyph when every date is reported) and a track that maps the row's phase bars and
 * milestone markers to left%/width% against the shared domain.
 *
 * @param {object} props - The visible row, domain, selection, year ticks, drag preview,
 *   and the marker mousedown handler.
 * @return {JSX.Element} The property row.
 */
function PropertyRow({
  visible,
  domain,
  selection,
  order,
  yearTicks,
  dragPreview,
  onMarkerMouseDown,
}: {
  visible: VisibleRow;
  domain: { startMs: number; endMs: number };
  selection: SelectionSet;
  order: MilestoneKey[];
  yearTicks: Array<{ year: number; pct: number }>;
  dragPreview: DragPreview | null;
  onMarkerMouseDown: (
    event: React.MouseEvent,
    visible: VisibleRow,
    field: MilestoneKey,
    merged: MergedDate
  ) => void;
}) {
  const { line1, line2 } = rowLabelLines(visible.row);
  const segments = phaseSegments(visible.dates, domain.endMs);

  return (
    <div className={styles.propertyRow}>
      <div className={styles.labelCol}>
        <span className={styles.labelLine1}>
          {visible.fullyLocked && (
            <Locked
              size={16}
              className={styles.lockIcon}
              aria-label="All dates reported (locked)"
            />
          )}
          <span className={styles.labelText}>{line1}</span>
        </span>
        {line2 && <span className={styles.labelLine2}>{line2}</span>}
      </div>
      <div className={styles.track} data-track>
        {yearTicks.map((tick) => (
          <div
            key={tick.year}
            className={styles.gridline}
            style={{ left: `${tick.pct}%` }}
          />
        ))}
        {segments.map((seg) => {
          const left = toPercent(seg.startMs, domain.startMs, domain.endMs);
          const width =
            toPercent(seg.endMs, domain.startMs, domain.endMs) - left;
          if (width <= 0) {
            return null;
          }
          return (
            <div
              key={seg.phase}
              className={styles.bar}
              data-phase={seg.phase}
              style={{
                left: `${left}%`,
                width: `${width}%`,
                backgroundColor: PHASE_COLORS[seg.phase],
              }}
            />
          );
        })}
        {MILESTONE_ORDER.map((field) => {
          const merged = visible.dates[field];
          const state = markerState(merged);
          const trueMs = parseIsoDay(merged?.value);
          if (state === null || trueMs === null) {
            return null;
          }
          // The previewed date while a drag is in flight: the grabbed marker follows the
          // pointer; every OTHER selected marker shifts by the same delta, clamped to its
          // own lifecycle bounds (mirrors the commit). Idle markers keep their true value.
          const primary = dragPreview?.primary;
          const isPrimary =
            primary?.slotId === visible.row.slotId && primary.field === field;
          let effIso = merged.value as string;
          if (isPrimary) {
            effIso = primary.iso;
          } else if (
            dragPreview &&
            dragPreview.participants.has(
              selectionKey(visible.row.slotId, field)
            )
          ) {
            const { minMs, maxMs } = neighborBounds(
              visible.dates,
              field,
              order
            );
            effIso = toIsoDay(
              clamp(trueMs + dragPreview.deltaMs, minMs, maxMs)
            );
          }
          const effMs = parseIsoDay(effIso) ?? trueMs;
          const { displayMs } = clampDisplayMs(effMs, domain.startMs);
          const left = toPercent(displayMs, domain.startMs, domain.endMs);
          const editable = isEditablePoint(merged);
          const isSelected = selection.has(
            selectionKey(visible.row.slotId, field)
          );
          return (
            <button
              key={field}
              type="button"
              tabIndex={-1}
              data-marker=""
              data-slot-id={visible.row.slotId}
              data-field={field}
              className={classNames(styles.marker, {
                [styles.markerEditable]: editable,
                [styles.markerSelected]: isSelected,
              })}
              style={{
                left: `${left}%`,
                borderColor: MARKER_COLORS[state],
                backgroundColor: isSelected ? MARKER_COLORS[state] : undefined,
              }}
              aria-label={`${MILESTONE_LABELS[field]} ${moment(effIso).format(
                'MM/DD/YYYY'
              )}`}
              onMouseDown={(event) =>
                onMarkerMouseDown(event, visible, field, merged)
              }
            />
          );
        })}
      </div>
    </div>
  );
}
